import gsap from "gsap";
import { useEffect, useRef, type ButtonHTMLAttributes } from "react";
export function Button({
  className = "",
  variant = "primary",
  onPointerEnter,
  onPointerMove,
  onPointerLeave,
  onPointerDown,
  onPointerUp,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(
    () => () => {
      if (buttonRef.current) gsap.killTweensOf(buttonRef.current);
    },
    [],
  );

  function settle() {
    if (!buttonRef.current || reduceMotion) return;
    gsap.to(buttonRef.current, {
      x: 0,
      y: 0,
      rotateX: 0,
      rotateY: 0,
      scale: 1,
      "--liquid-spread": 0,
      duration: 0.42,
      ease: "power3.out",
      overwrite: true,
    });
  }

  const styles = {
    primary:
      "bg-white/[.045] text-brand hover:bg-white/[.065] shadow-[0_0_24px_rgba(168,230,29,.08)]",
    secondary:
      "border border-white/[.12] bg-white/[.055] text-white hover:border-white/25 hover:bg-white/[.09]",
    ghost: "text-[#A0A5A8] hover:bg-white/[.05] hover:text-white",
    danger:
      "bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/25",
  };
  return (
    <button
      ref={buttonRef}
      data-variant={variant}
      className={`liquid-glass-button inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand/70 focus:ring-offset-2 focus:ring-offset-ink disabled:cursor-not-allowed disabled:opacity-50 ${styles[variant]} ${className}`}
      onPointerEnter={(event) => {
        onPointerEnter?.(event);
        if (!buttonRef.current || reduceMotion || props.disabled) return;
        gsap.to(buttonRef.current, {
          y: -1,
          scale: 1.006,
          "--liquid-spread": 1,
          duration: 0.65,
          ease: "power3.out",
          overwrite: true,
        });
      }}
      onPointerMove={(event) => {
        onPointerMove?.(event);
      }}
      onPointerLeave={(event) => {
        onPointerLeave?.(event);
        settle();
      }}
      onPointerDown={(event) => {
        onPointerDown?.(event);
        if (!buttonRef.current || reduceMotion || props.disabled) return;
        gsap.to(buttonRef.current, {
          scale: 0.985,
          duration: 0.18,
          ease: "power2.out",
          overwrite: true,
        });
      }}
      onPointerUp={(event) => {
        onPointerUp?.(event);
        settle();
      }}
      {...props}
    >
      <span className="relative z-[1] inline-flex items-center justify-center gap-2">
        {props.children}
      </span>
    </button>
  );
}
